import React from 'react';
import { useSelector } from 'react-redux';
import { selectKategorien } from 'modules/finanzen/features/finanzenSlice';
import { mischeKategorien, TYP_EINNAHME } from 'modules/finanzen/features/konstanten';
import { formatKurs, formatDatum } from 'core/utils/format';

/**
 * Wiederverwendbare Kontroll-Ansicht fuer vorbereitete Buchungen (Kontoauszug-
 * Import und Kontowecker-Mails): links die Liste mit Auswahl + Kategorie-Dropdown
 * je Zeile, rechts die Originaldaten der markierten Zeile. Reine Darstellung –
 * Auswahl/Kategorie werden ueber Callbacks nach oben gemeldet.
 */
export default function BuchungPruefliste({
  zeilen, markiert, onMarkieren, onKategorie, onAktiv, onAlle, onImport, zielName, importLabel = 'importieren',
}) {
  const eigene = useSelector(selectKategorien);
  const anzahlAktiv = zeilen.filter((z) => z.aktiv).length;
  const markierteZeile = zeilen.find((z) => z.schluessel === markiert);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PruefLeiste
        anzahlAktiv={anzahlAktiv}
        gesamt={zeilen.length}
        zielName={zielName}
        importLabel={importLabel}
        onAlle={onAlle}
        onImport={onImport}
      />
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 md:grid-cols-[1.4fr_1fr]">
        <ul className="min-h-0 divide-y divide-line overflow-y-auto border-t border-line">
          {zeilen.map((z) => (
            <PruefZeile
              key={z.schluessel}
              zeile={z}
              eigene={eigene}
              istMarkiert={z.schluessel === markiert}
              onMarkieren={() => onMarkieren(z.schluessel)}
              onKategorie={(k) => onKategorie(z.schluessel, k)}
              onAktiv={(a) => onAktiv(z.schluessel, a)}
            />
          ))}
        </ul>
        <RohAnsicht zeile={markierteZeile} />
      </div>
    </div>
  );
}

/** Werkzeugleiste ueber der Pruefliste. */
function PruefLeiste({ anzahlAktiv, gesamt, zielName, importLabel, onAlle, onImport }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
      <div className="flex items-center gap-3 text-sm text-ink-muted">
        <span>{anzahlAktiv} von {gesamt} ausgewählt</span>
        {zielName && <span className="text-ink">→ {zielName}</span>}
        <button type="button" onClick={() => onAlle(true)} className="underline hover:text-ink">Alle</button>
        <button type="button" onClick={() => onAlle(false)} className="underline hover:text-ink">Keine</button>
      </div>
      <button
        type="button"
        onClick={onImport}
        disabled={anzahlAktiv === 0}
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
      >
        {anzahlAktiv} {importLabel}
      </button>
    </div>
  );
}

/** Eine Zeile der Pruefliste: Auswahl, Betrag, Notiz und Kategorie-Dropdown. */
function PruefZeile({ zeile, eigene, istMarkiert, onMarkieren, onKategorie, onAktiv }) {
  const einnahme = zeile.typ === TYP_EINNAHME;
  const ton = einnahme ? 'text-gain' : 'text-loss';
  const kategorien = mischeKategorien(zeile.typ, eigene);
  return (
    <li
      onClick={onMarkieren}
      className={`flex cursor-pointer items-center gap-3 px-4 py-2 transition-colors ${
        istMarkiert ? 'bg-brand-600/10' : 'hover:bg-ink/5'
      } ${zeile.aktiv ? '' : 'opacity-50'}`}
    >
      <input
        type="checkbox"
        checked={zeile.aktiv}
        onChange={(e) => onAktiv(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 shrink-0 accent-brand-600"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{zeile.notiz || '—'}</p>
        <p className="text-xs text-ink-muted">{formatDatum(zeile.datum)}</p>
      </div>
      <span className={`shrink-0 text-sm font-semibold tabular-nums ${ton}`}>
        {einnahme ? '+' : '−'} {formatKurs(zeile.betrag)}
      </span>
      <select
        value={zeile.kategorie}
        onChange={(e) => onKategorie(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className="w-36 shrink-0 rounded-lg border border-line bg-paper px-2 py-1 text-xs text-ink focus:border-brand-600 focus:outline-none"
      >
        {kategorien.map((k) => (
          <option key={k.name} value={k.name}>{k.icon} {k.name}</option>
        ))}
      </select>
    </li>
  );
}

/** Rechte Seite: Originaldaten der markierten Buchung (Feldliste oder Rohtext). */
function RohAnsicht({ zeile }) {
  if (!zeile) {
    return (
      <div className="hidden items-center justify-center border-l border-t border-line p-6 text-sm text-ink-muted md:flex">
        Zeile links wählen, um die Originaldaten zu sehen.
      </div>
    );
  }
  return (
    <div className="hidden min-h-0 overflow-y-auto border-l border-t border-line bg-paper-soft p-5 md:block">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">Originaldaten</h3>
      {zeile.rohText ? (
        <pre className="whitespace-pre-wrap break-words text-xs text-ink">{zeile.rohText}</pre>
      ) : (
        <dl className="space-y-2">
          {(zeile.rohFelder || []).map((f) => (
            <div key={f.label} className="grid grid-cols-[9rem_1fr] gap-2">
              <dt className="text-xs text-ink-muted">{f.label}</dt>
              <dd className="break-words text-xs text-ink">{f.wert}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
